import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../../config';
import { defaultCallsheet, saveCallsheet, getCallsheet, saveKeyPersonnel, getKeyPersonnel, autoSaveOnDayAdvance, getCallsheetByDay } from '../utils/callsheetHelpers';
import { getSchedulesForDay } from '../utils/scheduleData';

/**
 * Custom hook for managing callsheet data
 * @param {string} projectId - The ID of the project
 * @returns {Object} - Callsheet data and functions for managing it
 */
const useCallsheetData = (projectId, projectName = '') => {
  const [callsheet, setCallsheet] = useState(defaultCallsheet);
  const callsheetRef = useRef(defaultCallsheet);
  useEffect(() => { callsheetRef.current = callsheet; }, [callsheet]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({
    loading: false,
    error: null,
    success: false,
    callsheetId: null
  });
  // Prevent overlapping saves
  const isSavingRef = useRef(false);
  // Dev-only guards to avoid duplicate fetches under StrictMode
  const didFetchProjectRef = useRef(false);
  const didLoadExistingRef = useRef(false);
  const lastLoadedDayRef = useRef(null);
  const needsScheduleRefreshRef = useRef(false);
  const lastEditTimestampRef = useRef(0);
  // Track previous shooting day for auto-save logic
  const previousShootingDay = useRef(null);
  
  // Helper to save last edited day to localStorage
  const saveLastEditedDay = useCallback((day) => {
    if (projectId && day) {
      localStorage.setItem(`callsheet_${projectId}_lastEditedDay`, String(day));
    }
  }, [projectId, projectName]);

  useEffect(() => {
    if (!projectName) return;
    setCallsheet((prev) => {
      const lastName = prevProjectNameRef.current;
      if (prev.projectTitle && prev.projectTitle !== lastName) {
        return prev;
      }
      prevProjectNameRef.current = projectName;
      return { ...prev, projectTitle: projectName };
    });
  }, [projectName]);
  
  // Helper to get last edited day from localStorage
  const getLastEditedDay = useCallback(() => {
    if (!projectId) return null;
    const saved = localStorage.getItem(`callsheet_${projectId}_lastEditedDay`);
    return saved ? parseInt(saved, 10) : null;
  }, [projectId]);
  
  // Helper to convert 12-hour time format (e.g., "9:00 AM") to 24-hour format (e.g., "09:00")
  const convertTo24Hour = (time12h) => {
    if (!time12h) return null;
    // If already in 24-hour format (no AM/PM), return as-is
    if (!time12h.toUpperCase().includes('AM') && !time12h.toUpperCase().includes('PM')) {
      return time12h;
    }
    const match = time12h.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return time12h;
    let [, hours, minutes, period] = match;
    hours = parseInt(hours, 10);
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  // Helper to get call time for a shooting day from OneLiner breaks
  const getCallTimeForShootingDay = useCallback((shootingDay) => {
    const savedCallTimes = localStorage.getItem(`oneliner_dayCallTimes_${projectId}`);
    if (savedCallTimes) {
      try {
        const callTimes = JSON.parse(savedCallTimes);
        if (callTimes[shootingDay]) {
          // Convert from 12-hour format (OneLiner) to 24-hour format (callsheet)
          const time24h = convertTo24Hour(callTimes[shootingDay]);
          console.log(`Using OneLiner call time for Day ${shootingDay}: ${callTimes[shootingDay]} -> ${time24h}`);
          return time24h;
        }
      } catch (e) {
        console.warn('Error parsing call times from localStorage:', e);
      }
    }
    return null; // No OneLiner call time set
  }, [projectId]);

  // Helper to calculate date for a shooting day based on OneLiner custom dates or start date
  const getDateForShootingDay = useCallback((shootingDay) => {
    // First check for custom date set on the day break in OneLiner
    const savedDayDates = localStorage.getItem(`oneliner_dayDates_${projectId}`);
    if (savedDayDates) {
      try {
        const dayDates = JSON.parse(savedDayDates);
        if (dayDates[shootingDay]) {
          // Parse custom date as local time
          const [year, month, day] = dayDates[shootingDay].split('-').map(Number);
          console.log(`Using custom date for Day ${shootingDay}: ${dayDates[shootingDay]}`);
          return new Date(year, month - 1, day);
        }
      } catch (e) {
        console.warn('Error parsing day dates from localStorage:', e);
      }
    }
    
    // Fall back to calculating from start date
    const savedStartDate = localStorage.getItem(`oneliner_startDate_${projectId}`);
    if (savedStartDate) {
      // Parse date as local time (not UTC) by splitting the string
      const [year, month, day] = savedStartDate.split('-').map(Number);
      const startDate = new Date(year, month - 1, day); // month is 0-indexed
      const date = new Date(startDate);
      date.setDate(date.getDate() + (shootingDay - 1));
      return date;
    }
    return new Date(); // Default to today if no start date set
  }, [projectId]);
  
  // Auto-save state
  const [autoSaveEnabled] = useState(true); // Can be made configurable via props/settings
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);
  const AUTO_SAVE_DELAY = 5000; // 5 seconds after last change

  const prevProjectNameRef = useRef(projectName);

  // Fetch project details and calculate total shooting days from oneliner
  useEffect(() => {
    const fetchProjectDetailsAndShootingDays = async () => {
      if (!projectId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch project details
        const projectResponse = await axios.get(`/api/v1/projects/${projectId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'x-auth-token': localStorage.getItem('token')
          }
        });
        
        // Fetch day breaks from oneliner to calculate total shooting days
        let calculatedTotalDays = 1; // Default to 1 day
        try {
          console.log(`Fetching day breaks for project ${projectId} to calculate total shooting days...`);
          const breaksResponse = await axios.get(`/api/v1/scripts/${projectId}/breaks`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'x-auth-token': localStorage.getItem('token')
            }
          });
          
          console.log('Raw breaks response:', breaksResponse.data);
          
          // Also fetch scenes to validate day breaks against actual scene count
          let scenesResponse;
          try {
            scenesResponse = await axios.get(`/api/v1/scripts/project/${projectId}/scenes`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'x-auth-token': localStorage.getItem('token')
              }
            });
            console.log('Scenes response for validation:', scenesResponse.data);
          } catch (scenesErr) {
            console.warn('Could not fetch scenes for day break validation:', scenesErr);
          }
          
          // Handle both array and object response formats
          let dayBreaks = [];
          if (Array.isArray(breaksResponse.data)) {
            dayBreaks = breaksResponse.data;
            console.log('Breaks data is an array with', dayBreaks.length, 'items');
          } else if (breaksResponse.data.breaks && Array.isArray(breaksResponse.data.breaks)) {
            dayBreaks = breaksResponse.data.breaks;
            console.log('Breaks data is an object with breaks array containing', dayBreaks.length, 'items');
          } else {
            console.log('Breaks data structure not recognized:', typeof breaksResponse.data, breaksResponse.data);
          }
          
          console.log('All breaks found:', dayBreaks);
          
          // Filter for day breaks only (using same logic as OneLiner component)
          const onlyDayBreaks = dayBreaks.filter(brk => {
            const isDay = brk.type === 'DAY_BREAK'; // Match OneLiner BREAK_TYPES.DAY_BREAK exactly
            console.log(`Break ${JSON.stringify(brk)} - Is day break: ${isDay}`);
            return isDay;
          });
          
          console.log('Day breaks only:', onlyDayBreaks);
          
          // Validate day breaks against scene count to filter out orphaned breaks
          let validDayBreaks = onlyDayBreaks;
          if (scenesResponse && scenesResponse.data && scenesResponse.data.scenes) {
            const scenes = scenesResponse.data.scenes;
            const maxSceneIndex = scenes.length - 1;
            console.log(`Total scenes: ${scenes.length}, max scene index: ${maxSceneIndex}`);
            
            validDayBreaks = onlyDayBreaks.filter(brk => {
              const isValid = brk.afterSceneIndex <= maxSceneIndex;
              if (!isValid) {
                console.log(`Filtering out orphaned day break with afterSceneIndex ${brk.afterSceneIndex} (max: ${maxSceneIndex})`);
              }
              return isValid;
            });
            
            console.log(`Filtered ${onlyDayBreaks.length} day breaks to ${validDayBreaks.length} valid day breaks`);
          }
          
          // Calculate total shooting days from valid breaks
          if (validDayBreaks.length > 0) {
            // Sort day breaks by afterSceneIndex to get them in chronological order
            const sortedDayBreaks = validDayBreaks.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
            console.log('Sorted valid day breaks by scene index:', sortedDayBreaks.map(brk => ({ afterSceneIndex: brk.afterSceneIndex, id: brk._id })));
            
            // Each day break represents "End of Day X"
            // Total shooting days = number of valid day breaks
            // (since "End of Day 9" means we have 9 complete shooting days)
            calculatedTotalDays = validDayBreaks.length;
            console.log(`Found ${validDayBreaks.length} valid day breaks, calculated total shooting days: ${calculatedTotalDays}`);
            
            // Save custom dates and call times per shooting day to localStorage for callsheet to use
            const dayDates = {};
            const dayCallTimes = {};
            sortedDayBreaks.forEach((brk, index) => {
              const dayNumber = index + 1; // Day 1, Day 2, etc.
              if (brk.customDate) {
                dayDates[dayNumber] = brk.customDate;
                console.log(`Day ${dayNumber} has custom date: ${brk.customDate}`);
              }
              if (brk.customTime) {
                dayCallTimes[dayNumber] = brk.customTime;
                console.log(`Day ${dayNumber} has custom call time: ${brk.customTime}`);
              }
            });
            localStorage.setItem(`oneliner_dayDates_${projectId}`, JSON.stringify(dayDates));
            localStorage.setItem(`oneliner_dayCallTimes_${projectId}`, JSON.stringify(dayCallTimes));
            console.log('Saved day dates to localStorage:', dayDates);
            console.log('Saved day call times to localStorage:', dayCallTimes);
          } else {
            console.log('No valid day breaks found in the data');
          }
        } catch (breaksErr) {
          console.error('Error fetching day breaks for total shooting days calculation:', breaksErr);
          console.error('Error details:', breaksErr.response?.data || breaksErr.message);
          // Fall back to localStorage value set by OneLiner (if available)
          const savedTotal = localStorage.getItem(`oneliner_totalShootingDays_${projectId}`);
          if (savedTotal) {
            calculatedTotalDays = parseInt(savedTotal, 10) || 1;
            console.log(`Using localStorage fallback for totalShootingDays: ${calculatedTotalDays}`);
          }
        }
        
        // Load saved key personnel data from localStorage
        const savedKeyPersonnel = getKeyPersonnel(projectId);
        console.log('Loaded saved key personnel:', savedKeyPersonnel);
        
        // Update callsheet with project title, calculated total shooting days, and saved key personnel
        setCallsheet(prev => {
          const shootingDay = prev.shootingDay || 1;
          // Use helper function which checks custom dates first, then falls back to start date calculation
          const calculatedDate = getDateForShootingDay(shootingDay);
          console.log(`Calculated date for Day ${shootingDay}: ${calculatedDate.toDateString()}`);
          
          // Get call time from OneLiner - OneLiner is source of truth
          const onelinerCallTime = getCallTimeForShootingDay(shootingDay);
          const generalCallTime = onelinerCallTime || prev.generalCallTime || '07:00';
          
          return {
            ...prev,
            projectId,
            projectTitle: projectResponse.data.projectName || '',
            totalShootingDays: calculatedTotalDays,
            // Set date based on OneLiner custom date or start date + shooting day offset
            date: calculatedDate,
            // Set general call time from OneLiner if not already set
            generalCallTime,
            // Auto-populate key personnel fields if available
            ...(savedKeyPersonnel ? {
              director: savedKeyPersonnel.director || prev.director,
              producer: savedKeyPersonnel.producer || prev.producer,
              dop: savedKeyPersonnel.dop || prev.dop
            } : {})
          };
        });
        
      } catch (err) {
        console.error('Error fetching project details:', err);
        setError('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };
    
    if (didFetchProjectRef.current) return; // guard duplicate in dev
    didFetchProjectRef.current = true;
    fetchProjectDetailsAndShootingDays();
  }, [projectId]);

  // Load existing callsheet data from database on component mount
  // Prioritize loading the last edited day if available
  useEffect(() => {
    const loadExistingCallsheet = async () => {
      if (!projectId) return;
      const loadStartedAt = Date.now();

      const applyCallsheetData = (callData, shootingDayOverride) => {
        if (!callData) return false;
        if (lastEditTimestampRef.current > loadStartedAt) {
          console.log('Skipping applying callsheet (edits since load started)');
          return false;
        }
        const { schedule, nextDaySchedule, ...rest } = callData;
        setCallsheet(prev => {
          const resolvedPrevTitle = prev.projectTitle?.trim() || '';
          const resolvedIncomingTitle = rest.projectTitle?.trim() || '';
          const resolvedPropTitle = projectName?.trim() || '';
          return {
            ...prev,
            projectId,
            ...rest,
            schedule: prev.schedule,
            nextDaySchedule: prev.nextDaySchedule,
            shootingDay: shootingDayOverride ?? rest.shootingDay ?? prev.shootingDay,
            company: rest.company ?? prev.company,
            productionLogo: rest.productionLogo ?? prev.productionLogo,
            projectTitle: resolvedPrevTitle || resolvedIncomingTitle || resolvedPropTitle,
            totalShootingDays: prev.totalShootingDays > 1 ? prev.totalShootingDays : (rest.totalShootingDays || prev.totalShootingDays)
          };
        });
        return true;
      };

      try {
        const lastEditedDay = getLastEditedDay();

        if (lastEditedDay) {
          console.log(`Loading last edited day ${lastEditedDay} from localStorage preference`);
          try {
            const dayResponse = await axios.get(`${API_URL}/api/v1/callsheets/project/${projectId}/day/${lastEditedDay}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'x-auth-token': localStorage.getItem('token')
              },
              validateStatus: (status) => status === 404 || (status >= 200 && status < 300)
            });

            if (dayResponse.status !== 404 && dayResponse.data && applyCallsheetData(dayResponse.data, lastEditedDay)) {
              lastLoadedDayRef.current = lastEditedDay;
              return;
            }
          } catch (dayError) {
            console.log(`No callsheet found for Day ${lastEditedDay}, falling back to latest`);
          }
        }

        try {
          const response = await axios.get(`${API_URL}/api/v1/callsheets/project/${projectId}/latest`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'x-auth-token': localStorage.getItem('token')
            },
            validateStatus: (status) => status === 404 || (status >= 200 && status < 300)
          });

          if (response.status !== 404 && response.data?.callsheet && applyCallsheetData(response.data.callsheet)) {
            lastLoadedDayRef.current = response.data.callsheet.shootingDay || lastLoadedDayRef.current;
          }
        } catch (latestError) {
          console.log('No existing callsheet found or error loading latest:', latestError.message);
        }
      } catch (error) {
        console.log('Error while attempting to load existing callsheet data:', error.message);
      }
    };

    if (didLoadExistingRef.current) return;
    didLoadExistingRef.current = true;
    loadExistingCallsheet();
  }, [projectId, projectName]);

  // Update callsheet data
  const updateCallsheet = useCallback((fieldOrObject, value, options = {}) => {
    setCallsheet(prev => {
      let newData;
      
      // Handle both object updates and field/value updates
      if (typeof fieldOrObject === 'object') {
        // If first argument is an object, merge it with current callsheet
        newData = {
          ...prev,
          ...fieldOrObject
        };
        
        // If shooting day changed via object update, recalculate the date and call time
        if (fieldOrObject.shootingDay !== undefined && fieldOrObject.shootingDay !== prev.shootingDay && projectId) {
          newData.date = getDateForShootingDay(fieldOrObject.shootingDay);
          // Update call time from OneLiner, or default to 7:00 AM if no custom time set
          const onelinerCallTime = getCallTimeForShootingDay(fieldOrObject.shootingDay);
          newData.generalCallTime = onelinerCallTime || '07:00';
          console.log(`Shooting day changed to ${fieldOrObject.shootingDay}, calculated date: ${newData.date.toDateString()}, call time: ${newData.generalCallTime}`);
        }
        
        // Check if any key personnel fields were updated
        const keyPersonnelFields = ['director', 'producer', 'dop'];
        const hasKeyPersonnelUpdates = keyPersonnelFields.some(field => 
          fieldOrObject[field] !== undefined && fieldOrObject[field] !== prev[field]
        );
        
        // If key personnel was updated, save to localStorage
        if (hasKeyPersonnelUpdates && projectId) {
          console.log('Key personnel updated, saving to localStorage');
          saveKeyPersonnel(projectId, {
            director: fieldOrObject.director !== undefined ? fieldOrObject.director : prev.director,
            producer: fieldOrObject.producer !== undefined ? fieldOrObject.producer : prev.producer,
            dop: fieldOrObject.dop !== undefined ? fieldOrObject.dop : prev.dop
          });
        }
      } else {
        // Otherwise, update a specific field
        newData = {
          ...prev,
          [fieldOrObject]: value
        };
        
        // If shooting day changed, recalculate the date and call time based on OneLiner
        if (fieldOrObject === 'shootingDay' && projectId) {
          newData.date = getDateForShootingDay(value);
          // Update call time from OneLiner, or default to 7:00 AM if no custom time set
          const onelinerCallTime = getCallTimeForShootingDay(value);
          newData.generalCallTime = onelinerCallTime || '07:00';
          console.log(`Shooting day changed to ${value}, calculated date: ${newData.date.toDateString()}, call time: ${newData.generalCallTime}`);
        }
        
        // Check if the updated field is a key personnel field
        if (['director', 'producer', 'dop'].includes(fieldOrObject) && projectId) {
          console.log(`Key personnel field ${fieldOrObject} updated, saving to localStorage`);
          saveKeyPersonnel(projectId, {
            director: fieldOrObject === 'director' ? value : prev.director,
            producer: fieldOrObject === 'producer' ? value : prev.producer,
            dop: fieldOrObject === 'dop' ? value : prev.dop
          });
        }
      }
      
      // Mark as having unsaved changes for auto-save
      if (options.skipUnsavedFlag) {
        if (typeof options.setUnsaved === 'boolean') {
          hasUnsavedChangesRef.current = options.setUnsaved;
        }
      } else {
        hasUnsavedChangesRef.current = true;
        lastEditTimestampRef.current = Date.now();
      }
      
      return newData;
    });
  }, [projectId]);

  // Auto-save effect - triggers after changes with debounce
  useEffect(() => {
    // Don't auto-save during initial load or if disabled
    if (!autoSaveEnabled || !initialLoadCompleteRef.current || !projectId) {
      return;
    }

    // Don't auto-save if no unsaved changes
    if (!hasUnsavedChangesRef.current) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for auto-save
    autoSaveTimerRef.current = setTimeout(async () => {
      // Check again if we should save (avoid race conditions)
      if (!hasUnsavedChangesRef.current || isSavingRef.current) {
        return;
      }

      try {
        console.log('🔄 Auto-saving callsheet...');
        isSavingRef.current = true;
        
        const result = await saveCallsheet(callsheetRef.current, projectId, {
          autoSave: true,
          replaceExisting: true
        });
        
        if (result) {
          hasUnsavedChangesRef.current = false;
          setLastAutoSave(new Date());
          // Save the current shooting day as last edited day
          saveLastEditedDay(callsheetRef.current.shootingDay);
          console.log('✅ Auto-save completed successfully');
        }
      } catch (err) {
        console.error('❌ Auto-save failed:', err);
        // Don't clear hasUnsavedChanges so it will retry
      } finally {
        isSavingRef.current = false;
      }
    }, AUTO_SAVE_DELAY);

    // Cleanup timer on unmount or when callsheet changes again
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [callsheet, autoSaveEnabled, projectId]);

  // Mark initial load as complete after first data load
  useEffect(() => {
    if (callsheet && callsheet.projectId && !initialLoadCompleteRef.current) {
      // Small delay to ensure we don't auto-save immediately after load
      setTimeout(() => {
        initialLoadCompleteRef.current = true;
        hasUnsavedChangesRef.current = false; // Reset after initial load
      }, 1000);
    }
  }, [callsheet]);

  // Save last edited day whenever shooting day changes
  useEffect(() => {
    if (callsheet?.shootingDay && projectId && initialLoadCompleteRef.current) {
      saveLastEditedDay(callsheet.shootingDay);
    }
  }, [callsheet?.shootingDay, projectId, saveLastEditedDay]);

  // Listen for scenesReordered event from OneLiner to trigger schedule refresh
  useEffect(() => {
    const handleScenesReordered = async (event) => {
      if (event.detail?.projectId === projectId) {
        console.log('📋 useCallsheetData: Scenes reordered, saving pending changes first...');
        
        // IMPORTANT: Save any pending changes FIRST before fetching fresh data
        // This ensures location assignments are persisted before we merge
        if (hasUnsavedChangesRef.current && !isSavingRef.current) {
          try {
            // Clear any pending auto-save timer
            if (autoSaveTimerRef.current) {
              clearTimeout(autoSaveTimerRef.current);
            }
            console.log('📋 Saving pending changes before reorder merge...');
            isSavingRef.current = true;
            await saveCallsheet(callsheetRef.current, projectId, {
              autoSave: true,
              replaceExisting: true
            });
            hasUnsavedChangesRef.current = false;
            console.log('✅ Pending changes saved successfully');
          } catch (err) {
            console.error('❌ Failed to save pending changes:', err);
          } finally {
            isSavingRef.current = false;
          }
        }
        
        // Mark that we need to refresh the schedule
        needsScheduleRefreshRef.current = true;
        
        // Immediately fetch fresh schedule data from the OneLiner
        const day = callsheetRef.current?.shootingDay || 1;
        try {
          const { schedule: freshSchedule, nextDaySchedule: freshNextDaySchedule } = await getSchedulesForDay(projectId, day);
          console.log('📋 Fresh schedule data fetched:', { scheduleCount: freshSchedule?.length, nextDayCount: freshNextDaySchedule?.length });
          
          // Fetch saved callsheet from DATABASE to get persisted location assignments
          // Now this will include the changes we just saved above
          const savedCallsheet = await getCallsheetByDay(projectId, day);
          const savedSchedule = savedCallsheet?.schedule || [];
          const savedNextDaySchedule = savedCallsheet?.nextDaySchedule || [];
          console.log('📋 Saved schedule from database:', { savedCount: savedSchedule?.length, savedNextDayCount: savedNextDaySchedule?.length });
          
          // Merge fresh scene data with saved location data
          const mergeScheduleWithSavedLocations = (freshScenes, savedScenes) => {
            if (!Array.isArray(freshScenes)) return [];
            return freshScenes.map(scene => {
              const savedScene = savedScenes.find(s => 
                s.sceneNumber === scene.sceneNumber || 
                s.sceneNumber?.toString() === scene.sceneNumber?.toString()
              );
              if (savedScene) {
                return {
                  ...scene,
                  location: savedScene.location || scene.location,
                  setScene: savedScene.setScene || scene.setScene,
                  pages: savedScene.pages || scene.pages,
                };
              }
              return scene;
            });
          };
          
          // Update the callsheet with merged schedule data
          setCallsheet(prev => ({
            ...prev,
            schedule: Array.isArray(freshSchedule) ? mergeScheduleWithSavedLocations(freshSchedule, savedSchedule) : [],
            nextDaySchedule: Array.isArray(freshNextDaySchedule) ? mergeScheduleWithSavedLocations(freshNextDaySchedule, savedNextDaySchedule) : []
          }));
          
          needsScheduleRefreshRef.current = false;
        } catch (err) {
          console.error('Error fetching fresh schedule:', err);
          // Clear schedule to force Schedule component to refetch
          setCallsheet(prev => ({
            ...prev,
            schedule: [],
            nextDaySchedule: []
          }));
        }
        
        // Also dispatch event to tell Schedule component to refetch
        window.dispatchEvent(new CustomEvent('forceScheduleRefresh', { 
          detail: { projectId, shootingDay: callsheetRef.current?.shootingDay || 1 } 
        }));
      }
    };

    window.addEventListener('scenesReordered', handleScenesReordered);
    return () => {
      window.removeEventListener('scenesReordered', handleScenesReordered);
    };
  }, [projectId]);

  // Listen for breaksUpdated events from OneLiner so totalShootingDays stays in sync
  // even though both components stay mounted as sibling tabs.
  useEffect(() => {
    const handleBreaksUpdated = (event) => {
      if (event.detail?.projectId === projectId && event.detail?.totalShootingDays != null) {
        const newTotal = event.detail.totalShootingDays;
        console.log(`📋 useCallsheetData: Received breaksUpdated event, totalShootingDays = ${newTotal}`);
        setCallsheet(prev => {
          if (prev.totalShootingDays === newTotal) return prev;
          return { ...prev, totalShootingDays: newTotal };
        });
      }
    };

    window.addEventListener('breaksUpdated', handleBreaksUpdated);
    return () => {
      window.removeEventListener('breaksUpdated', handleBreaksUpdated);
    };
  }, [projectId]);

  // Cleanup on unmount - save any pending changes
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      // Final save on unmount if there are unsaved changes
      if (hasUnsavedChangesRef.current && projectId && !isSavingRef.current) {
        saveCallsheet(callsheetRef.current, projectId, {
          autoSave: true,
          replaceExisting: true
        }).catch(err => console.error('Final auto-save failed:', err));
      }
    };
  }, [projectId]);

  // Update schedule
  const updateSchedule = useCallback((scheduleData) => {
    console.log('Updating schedule with data:', scheduleData);
    
    // Check if we're receiving { schedule: [...], nextDaySchedule: [...] } or just the schedule array directly
    if (scheduleData && scheduleData.schedule) {
      // If we received an object with schedule property, use that
      updateCallsheet('schedule', scheduleData.schedule);
      
      // If nextDaySchedule is included, update that as well
      if (scheduleData.nextDaySchedule) {
        updateCallsheet('nextDaySchedule', scheduleData.nextDaySchedule);
      }
    } else if (Array.isArray(scheduleData)) {
      // Otherwise, assume we received the schedule array directly
      updateCallsheet('schedule', scheduleData);
    } else {
      console.warn('Invalid schedule data format received:', scheduleData);
    }
    
    // Log the updated callsheet state for debugging
    console.log('Callsheet after schedule update');
  }, [updateCallsheet]);

  // Auto-load schedules for the current and next day without visiting Schedule tab
  // Merges fresh scene data from OneLiner with saved location data from database
  useEffect(() => {
    const loadSchedules = async () => {
      if (!projectId) return;
      // Skip auto-load if we have unsaved changes
      if (hasUnsavedChangesRef.current) {
        console.log('Skipping schedule auto-load: unsaved changes exist');
        return;
      }
      const day = (callsheet && callsheet.shootingDay) ? callsheet.shootingDay : 1;
      try {
        // Load fresh scene data from OneLiner
        const { schedule: freshSchedule, nextDaySchedule: freshNextDaySchedule } = await getSchedulesForDay(projectId, day);
        
        // Load saved callsheet data from database to get location assignments
        const savedCallsheet = await getCallsheetByDay(projectId, day);
        const savedSchedule = savedCallsheet?.schedule || [];
        const savedNextDaySchedule = savedCallsheet?.nextDaySchedule || [];
        
        // Merge fresh scene data with saved location data
        const mergeScheduleWithSavedLocations = (freshScenes, savedScenes) => {
          if (!Array.isArray(freshScenes)) return [];
          return freshScenes.map(scene => {
            // Find matching saved scene by sceneNumber
            const savedScene = savedScenes.find(s => 
              s.sceneNumber === scene.sceneNumber || 
              s.sceneNumber?.toString() === scene.sceneNumber?.toString()
            );
            if (savedScene) {
              // Preserve user-edited fields from saved data
              return {
                ...scene,
                location: savedScene.location || scene.location,
                setScene: savedScene.setScene || scene.setScene,
                // Keep other user-editable fields if they exist
                pages: savedScene.pages || scene.pages,
              };
            }
            return scene;
          });
        };
        
        if (Array.isArray(freshSchedule)) {
          const mergedSchedule = mergeScheduleWithSavedLocations(freshSchedule, savedSchedule);
          updateCallsheet('schedule', mergedSchedule);
        }
        if (Array.isArray(freshNextDaySchedule)) {
          const mergedNextDaySchedule = mergeScheduleWithSavedLocations(freshNextDaySchedule, savedNextDaySchedule);
          updateCallsheet('nextDaySchedule', mergedNextDaySchedule);
        }
      } catch (e) {
        console.warn('Could not auto-load schedules:', e?.message || e);
      }
    };
    loadSchedules();
  }, [projectId, callsheet?.shootingDay, updateCallsheet]);

  // When shooting day changes, load that day's callsheet and map all key fields so printing works without visiting tabs
  useEffect(() => {
    const loadDayData = async () => {
      if (!projectId) return;
      const dayNum = parseInt(callsheet?.shootingDay) || 1;
      // Skip if we've already loaded for this day to avoid oscillation
      if (lastLoadedDayRef.current === dayNum) return;
      // Skip if there are unsaved changes to avoid overwriting user edits
      if (hasUnsavedChangesRef.current) {
        console.log('Skipping day data load: unsaved changes exist');
        return;
      }
      const loadStartedAt = Date.now();
      try {
        const dayCs = await getCallsheetByDay(projectId, dayNum);
        if (dayCs) {
          // Map server callsheet to local state shape
          const tc = Array.isArray(dayCs.talentCalls) ? dayCs.talentCalls : [];
          const bg = Array.isArray(dayCs.background) ? dayCs.background : [];
          const cc = Array.isArray(dayCs.crewCalls) ? dayCs.crewCalls : [];
          // Only use saved schedule if we don't need a refresh (scenes weren't reordered)
          const schedule = needsScheduleRefreshRef.current ? [] : (Array.isArray(dayCs.schedule) ? dayCs.schedule : []);
          const locations = Array.isArray(dayCs.locations) ? dayCs.locations : [];
          const mainLoc = locations[0] || {};
          const additionalLocations = locations.length > 1 ? locations.slice(1) : [];
          const notes = dayCs.notes || {};
          setCallsheet(prev => {
            const resolvedDayTitle = (dayCs.projectTitle && dayCs.projectTitle.trim()) ? dayCs.projectTitle : '';
            const resolvedPrevTitle = (prev.projectTitle && prev.projectTitle.trim()) ? prev.projectTitle : '';
            const resolvedPropTitle = (projectName && projectName.trim()) ? projectName : '';
            return {
              ...prev,
              projectId,
              projectTitle: resolvedDayTitle || resolvedPrevTitle || resolvedPropTitle,
              company: dayCs.company ?? prev.company,
              productionLogo: dayCs.productionLogo ?? prev.productionLogo,
            // Calculate date based on OneLiner start date + shooting day offset
            date: getDateForShootingDay(dayCs.shootingDay ?? prev.shootingDay),
            shootingDay: dayCs.shootingDay ?? prev.shootingDay,
            // Always use the calculated totalShootingDays from project details (prev) over saved day data
            totalShootingDays: prev.totalShootingDays > 1 ? prev.totalShootingDays : (dayCs.totalShootingDays ?? prev.totalShootingDays),
            director: dayCs.director ?? prev.director,
            producer: dayCs.producer ?? prev.producer,
            dop: dayCs.dop ?? prev.dop,
            // Day-specific general call time: OneLiner is source of truth, default to 7:00 AM if not set
            generalCallTime: getCallTimeForShootingDay(dayCs.shootingDay ?? prev.shootingDay) || dayCs.generalCallTime || '07:00',
            weatherNotes: (dayCs.weatherNotes ?? dayCs.generalInfo?.weatherForecast ?? prev.weatherNotes),
            generalInfo: dayCs.generalInfo ? { ...prev.generalInfo, ...dayCs.generalInfo } : prev.generalInfo,
            // Locations mapping
            mainLocation: mainLoc.name || '',
            mainLocationAddress: mainLoc.address || '',
            mainLocationDirections: mainLoc.directions || '',
            mainLocationNotes: mainLoc.notes || '',
            additionalLocations,
            // Core arrays - always use fresh schedule from OneLiner, never from saved callsheet
            schedule: prev.schedule,
            crewCalls: cc,
            talentCalls: tc,
            background: bg,
            // Producers array (multi-producer support)
              producers: Array.isArray(dayCs.producers) ? dayCs.producers : prev.producers,
              // Notes mapping
              generalNotes: notes.general || '',
              parkingNotes: notes.parking || '',
              safetyNotes: notes.safety || '',
              cameraNotes: notes.camera || '',
              lightingNotes: notes.lighting || '',
              propsNotes: notes.props || '',
              soundNotes: notes.sound || '',
              vfxNotes: notes.vfx || '',
              specialFxNotes: notes.specialFx || '',
              vehiclesNotes: notes.vehicles || '',
              stuntsNotes: notes.stunts || '',
              animalsNotes: notes.animals || '',
              hairMakeupNotes: notes.hairMakeup || '',
              miscNotes: notes.misc || '',
              // Misc
              emergencyContacts: dayCs.emergencyContacts || prev.emergencyContacts,
              parkingLocations: Array.isArray(dayCs.parkingLocations) ? dayCs.parkingLocations : prev.parkingLocations,
              meals: dayCs.meals || prev.meals,
              // Carry through scheduleInfo block (Dates from server)
              scheduleInfo: dayCs.scheduleInfo || prev.scheduleInfo,
            };
          });
          lastLoadedDayRef.current = dayNum;
        } else {
          // No callsheet for this day; clear talent/background/notes so nothing appears
          if (lastEditTimestampRef.current > loadStartedAt) {
            console.log('Skipping clearing day data (edits since load started)');
            return;
          }
          setCallsheet(prev => ({
            ...prev,
            schedule: [],
            crewCalls: [],
            talentCalls: [],
            background: [],
            additionalLocations: [],
            parkingLocations: [],
            // Clear all notes for new day
            generalNotes: '',
            parkingNotes: '',
            safetyNotes: '',
            weatherNotes: '',
            cameraNotes: '',
            lightingNotes: '',
            propsNotes: '',
            soundNotes: '',
            vfxNotes: '',
            specialFxNotes: '',
            vehiclesNotes: '',
            stuntsNotes: '',
            animalsNotes: '',
            hairMakeupNotes: '',
            miscNotes: '',
            // Reset times to defaults when advancing to a day with no saved callsheet
            generalCallTime: getCallTimeForShootingDay(dayNum) || '07:00',
            scheduleInfo: {
              crewCall: null,
              talent1stCall: null,
              shootingCall: null,
              estimatedWrap: null,
              lunchTime: null
            }
          }));
          lastLoadedDayRef.current = dayNum;
        }
      } catch (err) {
        // On error (including 404), clear to empty to match expectation
        if (lastEditTimestampRef.current > loadStartedAt) {
          console.log('Skipping error fallback (edits since load started)');
          return;
        }
        setCallsheet(prev => ({
          ...prev,
          schedule: [],
          crewCalls: [],
          talentCalls: [],
          background: [],
        }));
        lastLoadedDayRef.current = dayNum;
      }
    };
    loadDayData();
  }, [projectId, callsheet?.shootingDay, projectName]);

  // Ensure parkingLocations are available without visiting Notes by loading from localStorage
  useEffect(() => {
    if (!projectId) return;
    const day = parseInt(callsheet?.shootingDay) || 1;
    const storageKey = `parkingLocations_${projectId}_${day}`;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Only set if current state has none to avoid clobbering server-loaded values
        if (!Array.isArray(callsheet?.parkingLocations) || callsheet.parkingLocations.length === 0) {
          updateCallsheet({ parkingLocations: parsed });
        }
      }
    } catch (_) {
      // ignore
    }
  }, [projectId, callsheet?.shootingDay]);

  // Update talent calls
  const updateTalentCalls = useCallback((newTalentCalls) => {
    updateCallsheet('talentCalls', newTalentCalls);
  }, [updateCallsheet]);

  // Update crew calls
  const updateCrewCalls = useCallback((crewCallsData) => {
    // Check if we're receiving { crewCalls: [...] } or just the array directly
    if (crewCallsData && crewCallsData.crewCalls) {
      // If we received an object with crewCalls property, use that
      updateCallsheet('crewCalls', crewCallsData.crewCalls);
    } else {
      // Otherwise, assume we received the array directly
      updateCallsheet('crewCalls', crewCallsData);
    }
  }, [updateCallsheet]);

  // Update emergency contacts
  const updateEmergencyContacts = useCallback((newContacts) => {
    updateCallsheet('emergencyContacts', newContacts);
  }, [updateCallsheet]);

  // Update additional locations
  const updateAdditionalLocations = useCallback((newLocations) => {
    updateCallsheet('additionalLocations', newLocations);
  }, [updateCallsheet]);

  // Save callsheet
  const handleSaveCallsheet = useCallback(async () => {
    // Avoid overlapping saves which can cause intermittent server errors
    if (isSavingRef.current || saveStatus.loading) return;
    isSavingRef.current = true;
    if (!projectId) {
      setSaveStatus({
        loading: false,
        error: 'No project ID provided',
        success: false,
        callsheetId: null
      });
      isSavingRef.current = false;
      return;
    }

    setSaveStatus({
      loading: true,
      error: null,
      success: false,
      callsheetId: null
    });

    try {
      const result = await saveCallsheet(callsheet, projectId);
      setSaveStatus({
        loading: false,
        error: null,
        success: true,
        callsheetId: result._id || result.id
      });
      return result;
    } catch (err) {
      console.error('Error saving callsheet:', err);
      setSaveStatus({
        loading: false,
        error: err.message || 'Failed to save callsheet',
        success: false,
        callsheetId: null
      });
      return null;
    } finally {
      isSavingRef.current = false;
    }
  }, [callsheet, projectId]);

  // Load callsheet by ID
  const loadCallsheet = useCallback(async (callsheetId) => {
    if (!callsheetId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getCallsheet(callsheetId);
      
      // Debug logging to see what data we received
      console.log('🔍 DEBUG: Raw data from getCallsheet:', data);
      console.log('🔍 DEBUG: Data fields check:', {
        hasProjectTitle: !!data.projectTitle,
        hasDirector: !!data.director,
        hasProducer: !!data.producer,
        hasDop: !!data.dop,
        hasSchedule: !!data.schedule,
        scheduleLength: data.schedule?.length || 0,
        hasCrewCalls: !!data.crewCalls,
        crewCallsLength: data.crewCalls?.length || 0,
        hasTalentCalls: !!data.talentCalls,
        talentCallsLength: data.talentCalls?.length || 0,
        hasNotes: !!data.notes,
        hasEmergencyContacts: !!data.emergencyContacts,
        hasLocations: !!data.locations,
        locationsLength: data.locations?.length || 0
      });
      
      // Map the server data structure to our component's structure
      const mappedCallsheet = {
        projectTitle: data.projectTitle || '',
        company: data.company || '',
        productionLogo: data.productionLogo || '',
        date: new Date(data.date),
        shootingDay: data.shootingDay || 1,
        totalShootingDays: data.totalShootingDays || 20,
        director: data.director || '',
        producer: data.producer || '',
        producers: Array.isArray(data.producers) ? data.producers : [],
        dop: data.dop || '',
        weatherNotes: (data.weatherNotes || data.generalInfo?.weatherForecast || ''),
        generalInfo: data.generalInfo || {},
        mainLocation: data.locations && data.locations.length > 0 ? data.locations[0].name : '',
        mainLocationAddress: data.locations && data.locations.length > 0 ? data.locations[0].address : '',
        mainLocationDirections: data.locations && data.locations.length > 0 ? data.locations[0].directions : '',
        mainLocationNotes: data.locations && data.locations.length > 0 ? data.locations[0].notes : '',
        additionalLocations: data.locations ? data.locations.slice(1) : [],
        schedule: data.schedule || [],
        crewCalls: data.crewCalls || [],
        talentCalls: data.talentCalls || [],
        background: data.background || [],
        generalNotes: data.notes?.general || '',
        parkingNotes: data.notes?.parking || '',
        safetyNotes: data.notes?.safety || '',
        // Per-category notes mapping from backend
        cameraNotes: data.notes?.camera || '',
        lightingNotes: data.notes?.lighting || '',
        propsNotes: data.notes?.props || '',
        soundNotes: data.notes?.sound || '',
        vfxNotes: data.notes?.vfx || '',
        specialFxNotes: data.notes?.specialFx || '',
        vehiclesNotes: data.notes?.vehicles || '',
        stuntsNotes: data.notes?.stunts || '',
        animalsNotes: data.notes?.animals || '',
        hairMakeupNotes: data.notes?.hairMakeup || '',
        miscNotes: data.notes?.misc || '',
        emergencyContacts: data.emergencyContacts || {
          police: '',
          fire: '',
          ambulance: '',
          hospital: '',
          productionOffice: '',
          productionManager: '',
          firstAD: '',
          locationManager: ''
        },
        // Include scheduleInfo from backend as-is (Dates)
        scheduleInfo: data.scheduleInfo || {}
      };
      
      setCallsheet(mappedCallsheet);
      return mappedCallsheet;
    } catch (err) {
      console.error('Error loading callsheet:', err);
      setError('Failed to load callsheet');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset callsheet to default
  const resetCallsheet = useCallback(() => {
    setCallsheet(defaultCallsheet);
  }, []);

  return {
    callsheet,
    callsheetRef,
    loading,
    error,
    saveStatus,
    lastAutoSave,
    updateCallsheet,
    updateSchedule,
    updateTalentCalls,
    updateCrewCalls,
    updateEmergencyContacts,
    updateAdditionalLocations,
    handleSaveCallsheet,
    loadCallsheet,
    resetCallsheet
  };
};

export default useCallsheetData;
